import {
  Avatar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { orange } from '@mui/material/colors';
import Container from '@mui/material/Container';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getInitials } from '../../components/Header/Header';
import { Heading } from '../../components/Heading';
import { Spinner } from '../../components/Spinner';
import { useAuth } from '../../contexts/authContext';
import { useTheme } from '../../contexts/themeContext';
import { getUsers, LeaderboardUser, User } from '../../firebase/api';

function isSearchMatch(user: User, searchQuery: string): boolean {
  return (
    user.nameLabel?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false
  );
}

const styles = {
  tableContainer: {
    maxHeight: 'calc(100vh - 280px)',
  },
  tableRow: {
    '&:last-child td': {
      borderBottom: 0,
    },
  },
  selfTableRow: {
    position: 'sticky',
    bottom: 0,
    top: 31,
    backgroundColor: 'background.paper',
    zIndex: 1,
    borderRadius: 1,
    boxShadow:
      '0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12)',

    '& td': {
      borderBottom: 0,
    },
  },
  hideBorderBelow: {
    '& td': {
      borderBottom: 0,
    },
  },
  borderLeftRadius: {
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4,
  },
  borderRightRadius: {
    borderTopRightRadius: 4,
    borderBottomRightRadius: 4,
  },
  twoLineTypography: {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    lineHeight: 1.4,
  },
};

function getRankBoxStyle(ranking: number, isTop3: boolean) {
  const style: { [key: string]: string | number } = {
    width: 40,
    textAlign: 'center',
  };
  if (isTop3) {
    style.borderRadius = '50%';
    style.paddingRight = 0;
    style.paddingTop = 4;
    style.paddingBottom = 4;
    style.fontSize = 20;
    style.color = 'rgba(0, 0, 0, 0.8)';
  }
  if (ranking === 1) {
    style.backgroundColor = '#F7C631';
    style.border = '2px solid #E3AA24';
  } else if (ranking === 2) {
    style.backgroundColor = '#D1CCC5';
    style.border = '2px solid #B3ACA8';
  } else if (ranking === 3) {
    style.backgroundColor = '#CE834A';
    style.border = '2px solid #B37314';
  }
  return style;
}

interface GameLeaderboardRowProps {
  user: LeaderboardUser;
  userAbove: LeaderboardUser | undefined;
  userBelow: LeaderboardUser | undefined;
  selfId: string | undefined;
  darkModeEnabled: boolean;
}

function GameLeaderboardRow({
  user,
  userAbove,
  userBelow,
  selfId,
  darkModeEnabled,
}: GameLeaderboardRowProps) {
  const navigate = useNavigate();
  const highscore = user.gameHighscore ?? 0;

  const isHighscoreSameAsAbove = (userAbove?.gameHighscore ?? 0) === highscore;
  const isHighscoreSameAsBelow = (userBelow?.gameHighscore ?? 0) === highscore;
  const isBelowSelf = userAbove?.id === selfId;
  const hideFields = isHighscoreSameAsAbove && !user.isSelf;

  const dividerBorder = darkModeEnabled
    ? 'rgba(81, 81, 81, 1)'
    : 'rgba(224, 224, 224, 1)';

  return (
    <TableRow
      onClick={() =>
        navigate(user.isSelf ? '/profile' : `/user/${user.id}`, {
          state: { prevPathname: '/game/leaderboard' },
        })
      }
      sx={[
        styles.tableRow,
        user.isSelf && styles.selfTableRow,
        isHighscoreSameAsBelow && styles.hideBorderBelow,
        isBelowSelf &&
          !isHighscoreSameAsAbove && {
            '& td': { borderTop: `1px solid ${dividerBorder}` },
          },
      ]}
    >
      <TableCell
        align="center"
        sx={[{ pl: 0.6, pr: 1.6 }, user.isSelf && styles.borderLeftRadius]}
      >
        {!hideFields && (
          <div style={getRankBoxStyle(user.ranking, user.isTop3)}>
            {!user.isTop3 && '#'}
            {user.ranking}
          </div>
        )}
      </TableCell>
      <TableCell sx={{ pl: 0 }}>
        {user.profilePicture !== undefined ? (
          <Avatar src={user.profilePicture} variant="rounded" />
        ) : (
          <Avatar variant="rounded" sx={{ bgcolor: orange[700] }}>
            {getInitials(user)}
          </Avatar>
        )}
      </TableCell>
      <TableCell sx={{ pl: 0 }}>
        <Typography variant="body2">{user.nameLabel}</Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={styles.twoLineTypography}
        >
          {user.associationLabel}
        </Typography>
      </TableCell>
      <TableCell
        align="right"
        sx={[{ pl: 0, pr: 1.6 }, user.isSelf && styles.borderRightRadius]}
      >
        {!hideFields && <Typography variant="body2">{highscore}</Typography>}
      </TableCell>
    </TableRow>
  );
}

export default function GameLeaderboardPage() {
  const { user: self } = useAuth();
  const { darkModeEnabled } = useTheme();
  const [users, setUsers] = useState<readonly User[] | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState('');

  const usersWithHighscore = useMemo(() => {
    if (users === undefined) return undefined;

    return users
      .filter(user => (user.gameHighscore ?? 0) > 0)
      .toSorted((a, b) => (b.gameHighscore ?? 0) - (a.gameHighscore ?? 0));
  }, [users]);

  const leaderboardUsers = useMemo(() => {
    if (usersWithHighscore === undefined) return undefined;

    let prevHighscore = -1;
    let prevRanking = -1;
    const filteredUsers: LeaderboardUser[] = [];
    for (const [index, user] of usersWithHighscore.entries()) {
      const highscore = user.gameHighscore ?? 0;
      const ranking = highscore !== prevHighscore ? index + 1 : prevRanking;

      if (searchQuery === '' || isSearchMatch(user, searchQuery)) {
        const isTop3 = ranking <= 3;
        const isSelf = user.id === self?.id;

        filteredUsers.push({ ...user, ranking, isTop3, isSelf });
      }

      prevHighscore = highscore;
      prevRanking = ranking;
    }

    return filteredUsers;
  }, [usersWithHighscore, searchQuery, self?.id]);

  const loaded = leaderboardUsers !== undefined;

  useEffect(() => {
    const fetchUsers = async () => {
      const fetchedUsers = await getUsers();
      setUsers(fetchedUsers);
    };

    fetchUsers();
  }, []);

  return (
    <Container component="main" maxWidth="sm">
      <Stack gap={4} py={2}>
        <Heading>Topplista</Heading>

        <Stack gap={2} mx={1}>
          <TextField
            label="Sök"
            type="search"
            value={searchQuery}
            onChange={event => setSearchQuery(event.target.value)}
            size="small"
          />

          {loaded ? (
            <TableContainer sx={styles.tableContainer}>
              <Table size="small" aria-label="game leaderboard" stickyHeader>
                <TableHead>
                  <TableRow
                    sx={{ '& > th': { bgcolor: 'background.default' } }}
                  >
                    <TableCell sx={{ minWidth: 57.6 }} />
                    <TableCell align="left" sx={{ px: 0, pt: 0 }}>
                      Capsare
                    </TableCell>
                    <TableCell sx={{ width: '100%' }} />
                    <TableCell sx={{ pt: 0, pl: 0, pr: 1.6 }}>Rekord</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {leaderboardUsers.map((user, index) => (
                    <GameLeaderboardRow
                      key={user.id}
                      user={user}
                      userAbove={leaderboardUsers[index - 1]}
                      userBelow={leaderboardUsers[index + 1]}
                      selfId={self?.id}
                      darkModeEnabled={darkModeEnabled}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Spinner />
          )}
        </Stack>
      </Stack>
    </Container>
  );
}
